/**
 * Professional Edit Page
 * Page for editing an existing healthcare professional
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { PageShell } from '../../components';
import ProfessionalForm from './components/ProfessionalForm';
import { professionalService } from '../../services/professionalService';
import { Professional, UpdateProfessionalRequest, CreateProfessionalRequest } from '../../types/professional';
import { useSnackbar } from 'notistack';

const ProfessionalEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [professional, setProfessional] = useState<Professional | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchProfessional = async () => {
      if (!id) {
        setError('ID do profissional não fornecido.');
        setLoading(false);
        return;
      }
      try {
        const fetchedProfessional = await professionalService.getProfessionalById(id);
        setProfessional(fetchedProfessional);
      } catch (err: unknown) {
        const apiError = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
        const errorMessage = (apiError.response?.data?.message || apiError.response?.data?.error || apiError.message) ?? 'Erro ao carregar dados do profissional';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchProfessional();
  }, [id, enqueueSnackbar]);


  const handleSubmit = async (data: UpdateProfessionalRequest | CreateProfessionalRequest) => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      await professionalService.updateProfessional(id, data as UpdateProfessionalRequest);
      enqueueSnackbar('Profissional atualizado com sucesso!', { variant: 'success' });
      /*
        Termina na FICHA, não na listagem: é ali que vivem as ARTs, e passar
        por lá ao salvar é o que as torna visíveis sem gastar um aviso na tela
        do formulário. `Cancelar` continua voltando para a listagem.
      */
      navigate(`/professionals/${id}`);
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = (apiError.response?.data?.message || apiError.response?.data?.error || apiError.message) ?? 'Erro ao atualizar profissional';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/professionals');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const breadcrumbs = [
    { label: 'Início', path: '/home' },
    { label: 'Profissionais', path: '/professionals' },
    { label: 'Editar' },
  ];

  if (error) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Profissional">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/professionals')} sx={{ mt: 2 }}>
          Voltar para a lista
        </Button>
      </PageShell>
    );
  }

  if (!professional) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Profissional">
        <Typography variant="h6">
          Profissional não encontrado.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/professionals')} sx={{ mt: 2 }}>
          Voltar para a lista
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={`Editar Profissional: ${professional.name}`}
    >
      <ProfessionalForm
        initialData={professional}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </PageShell>
  );
};

export default ProfessionalEditPage;
